import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  Injector,
  OnDestroy,
  signal,
  ViewChild
} from '@angular/core';
import {MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';
import {TranslatePipe} from '@ngx-translate/core';
import {ProfileSearchService} from './profile-search.service';

const DEBOUNCE_MS = 200;
/** Weiche Grenze laut NP20-824; reine Anzeige-Interaktion ohne Backend-Gegenstück. */
const MAX_QUERY_LENGTH = 200;
/** `app-modal` und `lib-modal` rendern inline, Material-Dialoge im CDK-Overlay. */
const DIALOG_SELECTOR = '.modal__dialog, .mat-mdc-dialog-container';

@Component({
  selector: 'app-profile-search',
  imports: [MatIconButton, MatIcon, MatTooltip, TranslatePipe],
  templateUrl: './profile-search.component.html',
  styleUrl: './profile-search.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Die Suchleiste selbst (Zähler, Ansage) darf nicht mitgezählt werden.
  host: {'data-profile-search-ignore': ''}
})
export class ProfileSearchComponent implements OnDestroy {
  @ViewChild('input') private input?: ElementRef<HTMLInputElement>;

  protected readonly search = inject(ProfileSearchService);
  protected readonly query = signal('');
  protected readonly maxLength = MAX_QUERY_LENGTH;

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private returnFocus: HTMLElement | null = null;

  readonly isOpen = this.search.isOpen;

  /**
   * Strg/Cmd+F gilt nur, solange die Profilseite (und damit diese Komponente) lebt. Ein
   * zweites Strg/Cmd+F bei fokussierter Suchleiste geht an die Browser-Suche.
   */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    const isFind = (event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey
      && event.key?.toLowerCase() === 'f';
    if (isFind) {
      if (this.isOpen() && document.activeElement === this.input?.nativeElement) return;
      // Ein offener Dialog liegt über der Leiste; sie bekäme sonst unsichtbar den Fokus.
      if (document.querySelector(DIALOG_SELECTOR)) return;
      event.preventDefault();
      this.open();
    } else if (event.key === 'Escape' && this.isOpen() && this.isOwnEscape(event)) {
      event.preventDefault();
      this.close();
    }
  }

  open(): void {
    if (!this.isOpen()) {
      const active = document.activeElement;
      this.returnFocus = active instanceof HTMLElement && active !== document.body ? active : null;
      this.search.open();
    }
    afterNextRender(() => {
      this.input?.nativeElement.focus();
      this.input?.nativeElement.select();
    }, {injector: this.injector});
  }

  toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  close(): void {
    this.cancelDebounce();
    this.query.set('');
    this.search.close();
    const target = this.returnFocus;
    this.returnFocus = null;
    if (target?.isConnected) target.focus();
  }

  onInput(value: string): void {
    this.query.set(value);
    this.cancelDebounce();
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.search.search(value);
    }, DEBOUNCE_MS);
  }

  onInputKeydown(event: KeyboardEvent): void {
    const backwards = event.key === 'ArrowUp' || (event.key === 'Enter' && event.shiftKey);
    const forwards = event.key === 'ArrowDown' || (event.key === 'Enter' && !event.shiftKey);
    if (!backwards && !forwards) return;
    event.preventDefault();
    // Direkt nach dem Tippen: erst suchen, nicht in der alten Liste springen. Die Suche
    // steht dann auf Treffer 1 — rückwärts heißt also der letzte.
    if (this.debounceTimer !== null) {
      this.cancelDebounce();
      this.search.search(this.query());
      if (backwards) void this.search.previous();
      return;
    }
    void (backwards ? this.search.previous() : this.search.next());
  }

  ngOnDestroy(): void {
    this.cancelDebounce();
  }

  private cancelDebounce(): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  /** Esc aus der Suchleiste oder ohne Fokus — nicht, wenn es gerade ein Dialog oder Select braucht. */
  private isOwnEscape(event: KeyboardEvent): boolean {
    const target = event.target;
    return target === document.body || target === document.documentElement
      || (target instanceof Node && this.host.nativeElement.contains(target));
  }
}
